import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, MessageSquare, Send } from 'lucide-react';
import { adminApi } from '../../services/api';
import { annoncerErreur, EtatErreur } from '../../etats';
import { Carte, champ, Chargement, fmtDateHeure, Vide } from './ui';

const RAFRAICHISSEMENT_MS = 20000;
const LIMITE = 5000;

/* Fil d'echanges entre l'equipe d'accompagnement et l'association. */
export default function EchangesProjet({ projetId, nomAssociation, peutEcrire = true }) {
  const [messages, setMessages] = useState(null);
  const [texte, setTexte] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState(null);
  const fin = useRef(null);

  const charger = useCallback(() => (
    adminApi.getMessagesProjet(projetId)
      .then((m) => { setMessages(m); setErreur(null); })
      // Un échec du rafraîchissement périodique garde le fil affiché.
      .catch((e) => setErreur(e))
  ), [projetId]);

  useEffect(() => {
    charger();
    const minuterie = setInterval(() => { if (document.visibilityState === 'visible') charger(); }, RAFRAICHISSEMENT_MS);
    return () => clearInterval(minuterie);
  }, [charger]);

  useEffect(() => { fin.current?.scrollIntoView({ block: 'nearest' }); }, [messages?.length]);

  const envoyer = async (e) => {
    e.preventDefault();
    const contenu = texte.trim();
    if (!contenu) return;
    setEnvoi(true);
    try {
      const message = await adminApi.envoyerMessageProjet(projetId, contenu);
      setMessages((liste) => [...(liste || []), message]);
      setTexte('');
    } catch (err) {
      annoncerErreur(err.message || "Le message n'a pas pu être envoyé.");
    } finally {
      setEnvoi(false);
    }
  };

  if (!messages) return erreur ? <EtatErreur erreur={erreur} onReessayer={charger} /> : <Chargement />;

  return (
    <Carte titre={`Échanges avec ${nomAssociation}`}>
      <div className="max-h-[60vh] overflow-y-auto space-y-3 pr-1 mb-4" aria-live="polite">
        {messages.length === 0 ? (
          <Vide icone={MessageSquare} titre="Aucun message pour l'instant"
            texte="Écrivez à l'association : ses dirigeants sont prévenus par e-mail et répondent depuis leur espace." />
        ) : messages.map((m) => (
          <div key={m.id} className={`flex ${m.cote === 'equipe' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] sm:max-w-[70%] rounded-carte px-4 py-2.5 ${m.cote === 'equipe'
              ? 'bg-[var(--accent)]/15 text-[var(--text-bold)] rounded-br-md'
              : 'bg-[var(--bg-main)] text-[var(--text-main)] rounded-bl-md'}`}>
              <p className="text-[11px] font-semibold text-[var(--text-muted)] mb-0.5">
                {m.de_moi ? 'Vous' : m.auteur || (m.cote === 'equipe' ? 'Équipe ASSO-PRO' : nomAssociation)} · {fmtDateHeure(m.date)}
              </p>
              <p className="text-sm whitespace-pre-line break-words">{m.contenu}</p>
              {m.cote === 'equipe' && m.lu_le && <p className="text-[10px] text-[var(--text-muted)] text-right mt-0.5">Lu</p>}
            </div>
          </div>
        ))}
        <div ref={fin} />
      </div>
      {!peutEcrire ? (
        <p className="text-xs text-[var(--text-muted)]">Consultation seule : l'échange avec l'association revient à son coach.</p>
      ) : (
      <form onSubmit={envoyer} className="flex flex-col sm:flex-row gap-2">
        <label className="sr-only" htmlFor="message-projet">Votre message</label>
        <textarea id="message-projet" value={texte} onChange={(e) => setTexte(e.target.value.slice(0, LIMITE))} rows={2}
          placeholder="Votre message…" className={`${champ} resize-y`}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) envoyer(e); }} />
        <button type="submit" disabled={envoi || !texte.trim()} aria-busy={envoi || undefined}
          className="act">
          {envoi ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}Envoyer
        </button>
      </form>
      )}
    </Carte>
  );
}